import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlignLeft, AlignCenter, AlignRight, ArrowUpToLine, FoldVertical, ArrowDownToLine } from "lucide-react";

export const FONT_FAMILIES = [
  { v: "", label: "Default" },
  { v: "Inter, sans-serif", label: "Inter" },
  { v: "Arial, sans-serif", label: "Arial" },
  { v: "Helvetica, sans-serif", label: "Helvetica" },
  { v: "Georgia, serif", label: "Georgia" },
  { v: "'Times New Roman', serif", label: "Times New Roman" },
  { v: "'Courier New', monospace", label: "Courier New" },
  { v: "Verdana, sans-serif", label: "Verdana" },
  { v: "Tahoma, sans-serif", label: "Tahoma" },
  { v: "'Trebuchet MS', sans-serif", label: "Trebuchet MS" },
];

// Inline CSS from a font-spec { family, size, color, bold, italic, underline, strike, align, valign }
export function fontCss(f = {}) {
  const s = {};
  if (f.family) s.fontFamily = f.family;
  if (f.size) s.fontSize = `${f.size}px`;
  if (f.color) s.color = f.color;
  if (f.bold) s.fontWeight = 700;
  if (f.italic) s.fontStyle = "italic";
  const deco = [f.underline && "underline", f.strike && "line-through"].filter(Boolean).join(" ");
  if (deco) s.textDecoration = deco;
  if (f.align) s.textAlign = f.align;
  if (f.valign) s.verticalAlign = { top: "top", middle: "middle", bottom: "bottom" }[f.valign];
  return s;
}

function TB({ on, onClick, testid, title, children }) {
  return (
    <button data-testid={testid} title={title} onClick={onClick}
      className={"w-6 h-6 rounded border text-[11px] leading-none flex items-center justify-center shrink-0 " + (on
        ? "bg-[color:var(--brand-ocean)] text-white border-[color:var(--brand-ocean)]"
        : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] border-[color:var(--glass-border)] hover:text-[color:var(--ink)]")}>
      {children}
    </button>
  );
}

// Compact reusable font/alignment control cluster; writes partial patches via onChange(patch).
export function FontControls({ value = {}, onChange, testid = "font", showAlign = true, showVAlign = false, sizeMin = 8, sizeMax = 40 }) {
  const f = value;
  const set = (p) => onChange(p);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Select value={f.family || ""} onValueChange={(v) => set({ family: v })}>
          <SelectTrigger data-testid={`${testid}-family`} className="bg-[color:var(--surface-2)] flex-1 min-w-0 h-6 text-[10px]"><SelectValue placeholder="Font" /></SelectTrigger>
          <SelectContent>{FONT_FAMILIES.map((x) => <SelectItem key={x.v || "def"} value={x.v || "__def__"}>{x.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid={`${testid}-size`} type="number" min={sizeMin} max={sizeMax} placeholder="px" value={f.size ?? ""}
          onChange={(e) => { const n = e.target.value === "" ? "" : Math.max(sizeMin, Math.min(sizeMax, Number(e.target.value))); set({ size: n }); }}
          className="bg-[color:var(--surface-2)] w-14 h-6 text-[10px] shrink-0" />
        <input data-testid={`${testid}-color`} type="color" title="Font color" value={f.color || "#33475b"} onChange={(e) => set({ color: e.target.value })}
          className="h-6 w-7 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <TB testid={`${testid}-bold`} title="Bold" on={f.bold} onClick={() => set({ bold: !f.bold })}><b>B</b></TB>
        <TB testid={`${testid}-italic`} title="Italic" on={f.italic} onClick={() => set({ italic: !f.italic })}><i>I</i></TB>
        <TB testid={`${testid}-underline`} title="Underline" on={f.underline} onClick={() => set({ underline: !f.underline })}><u>U</u></TB>
        {showAlign && (
          <span className="flex items-center gap-1 ml-1">
            <TB testid={`${testid}-align-left`} title="Align left" on={(f.align || "left") === "left"} onClick={() => set({ align: "left" })}><AlignLeft className="w-3 h-3" /></TB>
            <TB testid={`${testid}-align-center`} title="Align center" on={f.align === "center"} onClick={() => set({ align: "center" })}><AlignCenter className="w-3 h-3" /></TB>
            <TB testid={`${testid}-align-right`} title="Align right" on={f.align === "right"} onClick={() => set({ align: "right" })}><AlignRight className="w-3 h-3" /></TB>
          </span>
        )}
        {showVAlign && (
          <span className="flex items-center gap-1 ml-1">
            <TB testid={`${testid}-valign-top`} title="Align top" on={f.valign === "top"} onClick={() => set({ valign: "top" })}><ArrowUpToLine className="w-3 h-3" /></TB>
            <TB testid={`${testid}-valign-middle`} title="Align middle" on={(f.valign || "middle") === "middle"} onClick={() => set({ valign: "middle" })}><FoldVertical className="w-3 h-3" /></TB>
            <TB testid={`${testid}-valign-bottom`} title="Align bottom" on={f.valign === "bottom"} onClick={() => set({ valign: "bottom" })}><ArrowDownToLine className="w-3 h-3" /></TB>
          </span>
        )}
      </div>
    </div>
  );
}

// Font family select stores "__def__" for default; unwrap before persisting.
export function unwrapFamily(patch) {
  if (patch.family === "__def__") return { ...patch, family: "" };
  return patch;
}

export const BORDER_STYLES = ["solid", "dashed", "dotted", "double"];
